import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Building2, Loader2, RefreshCw } from 'lucide-react';
import { apiFetch } from '@/stores/authStore';
import { OrgTradersTable, type OrgTraderRow } from '@/components/organisms/OrgTradersTable';

interface OrgDetailProps {
  orgId: string;
  orgName?: string;
  onBack: () => void;
  onTraderClick: (row: OrgTraderRow) => void;
}

interface RawPosition {
  ticker?: string;
  market_ticker?: string;
  position?: number;
  market_exposure?: number;
}

interface RawOrder {
  order_id?: string;
  id?: string;
  ticker?: string;
  market_ticker?: string;
  side?: 'yes' | 'no';
  count?: number;
  remaining_count?: number;
  yes_price?: number;
  no_price?: number;
}

function truncateTicker(ticker: string): string {
  if (ticker.length <= 24) return ticker;
  const parts = ticker.split('-');
  if (parts.length >= 2) return `...${parts.slice(-2).join('-')}`;
  return `...${ticker.slice(-20)}`;
}

function toArr<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

export function OrgDetail({ orgId, orgName, onBack, onTraderClick }: OrgDetailProps) {
  const [balance, setBalance] = useState<Record<string, unknown> | null>(null);
  const [positions, setPositions] = useState<RawPosition[]>([]);
  const [orders, setOrders] = useState<RawOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const safe = async (url: string) => {
      try {
        const res = await apiFetch(url);
        const data = await res.json().catch(() => null);
        return { ok: res.ok, data };
      } catch {
        return { ok: false, data: null };
      }
    };

    const [balRes, posRes, ordRes] = await Promise.all([
      safe(`/api/admin/orgs/${orgId}/balance`),
      safe(`/api/admin/orgs/${orgId}/positions`),
      safe(`/api/admin/orgs/${orgId}/orders`),
    ]);

    setBalance(balRes.ok ? (balRes.data as Record<string, unknown>) : null);

    if (posRes.ok && posRes.data) {
      const posData = posRes.data as Record<string, unknown>;
      setPositions(toArr<RawPosition>(posData.market_positions ?? posData.positions));
    } else {
      setPositions([]);
    }

    if (ordRes.ok && ordRes.data) {
      const ordData = ordRes.data as Record<string, unknown>;
      setOrders(toArr<RawOrder>(ordData.orders));
    } else {
      setOrders([]);
    }

    if (![balRes, posRes, ordRes].some((r) => r.ok)) {
      setError('Could not load org activity. The org may not have a credential configured.');
    }
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const balanceCents = balance?.balance ?? balance?.available_balance ?? null;

  return (
    <div className="h-full overflow-auto p-6">
      <div className="mx-auto max-w-7xl space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1 rounded px-2 py-1 text-xs"
            >
              <ArrowLeft size={14} /> Back
            </button>
            <Building2 size={20} className="text-muted-foreground" />
            <h1 className="text-xl font-semibold">{orgName ?? 'Organization'}</h1>
            <span className="text-muted-foreground text-xs">org-aggregate (raw Kalshi)</span>
          </div>
          <button
            onClick={fetchAll}
            disabled={loading}
            className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1.5 transition-colors"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {error && (
          <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>
        )}

        {/* Org summary cards */}
        <div className="grid gap-3 grid-cols-3">
          <div className="bg-card border-border rounded-lg border px-4 py-3">
            <div className="text-muted-foreground text-xs">Org Balance</div>
            <div className="text-foreground mt-1 text-lg font-semibold">
              {balanceCents !== null
                ? `$${(Number(balanceCents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                : '—'}
            </div>
          </div>
          <div className="bg-card border-border rounded-lg border px-4 py-3">
            <div className="text-muted-foreground text-xs">Open Positions</div>
            <div className="text-foreground mt-1 text-lg font-semibold">{positions.length}</div>
          </div>
          <div className="bg-card border-border rounded-lg border px-4 py-3">
            <div className="text-muted-foreground text-xs">Open Orders</div>
            <div className="text-foreground mt-1 text-lg font-semibold">{orders.length}</div>
          </div>
        </div>

        {/* Org-level positions table */}
        <div className="bg-card border-border rounded-lg border">
          <div className="border-border border-b px-4 py-2.5">
            <h2 className="text-foreground text-sm font-medium">Org Positions (aggregate)</h2>
          </div>
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
            </div>
          ) : positions.length === 0 ? (
            <p className="text-muted-foreground px-4 py-6 text-center text-sm">No open positions</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">Market</th>
                  <th className="px-3 py-2 text-center">Side</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Notional</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p, i) => {
                  const ticker = p.ticker ?? p.market_ticker ?? '—';
                  const signed = Number(p.position ?? 0);
                  const side = signed >= 0 ? 'YES' : 'NO';
                  const qty = Math.abs(signed);
                  const exposure = Number(p.market_exposure ?? 0);
                  return (
                    <tr key={i} className="border-border/40 border-b">
                      <td className="text-foreground px-4 py-2 font-mono text-xs">
                        {truncateTicker(ticker)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                            side === 'YES'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {side}
                        </span>
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">{qty}</td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        ${(exposure / 100).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Org-level orders table */}
        <div className="bg-card border-border rounded-lg border">
          <div className="border-border border-b px-4 py-2.5">
            <h2 className="text-foreground text-sm font-medium">Org Orders (aggregate)</h2>
          </div>
          {orders.length === 0 ? (
            <p className="text-muted-foreground px-4 py-6 text-center text-sm">No open orders</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">Market</th>
                  <th className="px-3 py-2 text-center">Side</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Price</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o, i) => {
                  const ticker = o.ticker ?? o.market_ticker ?? '—';
                  const side = o.side ?? 'yes';
                  const qty = o.remaining_count ?? o.count ?? 0;
                  const price = side === 'yes' ? o.yes_price : o.no_price;
                  return (
                    <tr key={o.order_id ?? o.id ?? i} className="border-border/40 border-b">
                      <td className="text-foreground px-4 py-2 font-mono text-xs">
                        {truncateTicker(ticker)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                            side === 'yes'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {String(side).toUpperCase()}
                        </span>
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">{qty}</td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {price ? `${price}¢` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Per-trader rows */}
        <OrgTradersTable
          orgQueryParam={`?orgId=${orgId}`}
          onRowClick={onTraderClick}
          title="Members"
        />
      </div>
    </div>
  );
}
